// src/components/soldLeads/SoldLeadsTable.jsx
import React, { useMemo, useState, useEffect } from 'react';
import {
  HStack,
  Text,
  Input,
  Select,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Td,
  Th,
  Tag,
  Spinner,
  Tooltip,
  Box,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import DealCheckDrawer from './DealCheckDrawer';
import { STAGE_OPTIONS, VISIBLE_STAGE, stageLabel } from '../../hooks/useSoldLeads';

/** Column widths */
const COL = {
  status: '96px',
  stage: '136px',
  surveyorMin: '208px',
  appt: '144px',
  survey: '156px',
  assigned: '120px',
  deal: '112px',
};

/** Helpers */
const toDateObj = (val) => {
  if (!val) return null;
  try { return typeof val.toDate === 'function' ? val.toDate() : new Date(val); }
  catch { return null; }
};

const toSurveyDateObj = (val) => {
  if (!val) return null;
  try {
    const [y, m, d] = String(val).split('-');
    if (!y || !m || !d) return null;
    return new Date(Number(y), Number(m) - 1, Number(d));
  } catch {
    return null;
  }
};

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(d);
  } catch { return '—'; }
};

const fmtTime = (d) => {
  if (!d) return '—';
  try {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
  } catch { return '—'; }
};

const SLOT_OPTIONS = [
  '07:00–09:00', '09:00–11:00', '11:00–13:00', '13:00–15:00',
  '15:00–17:00', '17:00–19:00', '19:00–21:00',
];

const labelFromStart = (hhmm) => {
  if (!hhmm) return '';
  const idx = SLOT_OPTIONS.findIndex((s) => s.startsWith(hhmm));
  return idx >= 0 ? SLOT_OPTIONS[idx] : `${hhmm} (Custom)`;
};

const normaliseSlot = (raw) => {
  if (!raw) return '';
  let s = String(raw).trim();
  s = s.replace(/--/g, '–').replace(/\s*-\s*/g, '–').replace(/\s*—\s*/g, '–').replace(/\s*–\s*/g, '–');
  const parts = s.split('–');
  if (parts.length === 2) {
    const pad = (t) => {
      const [hh = '', mm = ''] = t.split(':');
      return `${String(hh).padStart(2, '0')}:${String(mm || '00').padStart(2, '0')}`;
    };
    s = `${pad(parts[0].trim())}–${pad(parts[1].trim())}`;
  }
  return s;
};

/** Comparators */
const cmpDate = (a, b) => {
  if (a && b) return a.getTime() - b.getTime();
  if (a && !b) return 1;
  if (!a && b) return -1;
  return 0;
};
const cmpStr = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' });

export default function SoldLeadsTable({
  rows,
  loading,
  cursor,
  loadingMore,
  search,
  setSearch,
  stageFilter,
  setStageFilter,
  onLoadMore,
  onOpenSalesReport, // kept for compatibility (unused)
  handlers,
}) {
  const [dealRow, setDealRow] = useState(null);

  // Keep the open drawer's row in sync with the latest "rows" data.
  const selectedId = dealRow?.leadId;
  useEffect(() => {
    if (!selectedId || !Array.isArray(rows) || rows.length === 0) return;
    const fresh = rows.find((r) => r.leadId === selectedId);
    if (fresh) setDealRow((prev) => (fresh !== prev ? fresh : prev));
  }, [rows, selectedId]);

  /** Filters */
  const filtered = useMemo(() => {
    const term = (search || '').trim().toLowerCase();
    return (rows || []).filter((r) => {
      const matchesText =
        !term ||
        (r.customer || '').toLowerCase().includes(term) ||
        (r.postcode || '').toLowerCase().includes(term) ||
        (r.salespersonName || '').toLowerCase().includes(term) ||
        (r.salespersonId || '').toLowerCase().includes(term);
      const matchesStage =
        stageFilter === 'all' || (r.postSaleStage || 'awaiting_survey') === stageFilter;
      return matchesText && matchesStage;
    });
  }, [rows, search, stageFilter]);

  /** Sorting (new) */
  const [sortBy, setSortBy] = useState('appt');     // 'appt' | 'surveyDate' | 'surveyor'
  const [sortDir, setSortDir] = useState('desc');   // 'asc' | 'desc'

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let base = 0;
      if (sortBy === 'appt') {
        const aD = toDateObj(a.appt) || toDateObj(a.apptStartAt);
        const bD = toDateObj(b.appt) || toDateObj(b.apptStartAt);
        base = cmpDate(aD, bD);
      } else if (sortBy === 'surveyDate') {
        const aD = toSurveyDateObj(a.surveyDate);
        const bD = toSurveyDateObj(b.surveyDate);
        base = cmpDate(aD, bD);
      } else if (sortBy === 'surveyor') {
        base = cmpStr(a.surveyorName, b.surveyorName);
      }
      return sortDir === 'asc' ? base : -base;
    });
    return arr;
  }, [filtered, sortBy, sortDir]);

  // Direction labels (dates vs names)
  const dirOptions = sortBy === 'surveyor'
    ? [{ value: 'asc', label: 'A → Z' }, { value: 'desc', label: 'Z → A' }]
    : [{ value: 'desc', label: 'Newest first' }, { value: 'asc', label: 'Oldest first' }];

  return (
    <>
      <HStack spacing={3} mb={4} align="center" flexWrap="wrap">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search customer, postcode, or salesperson"
          maxW="360px"
        />
        <HStack spacing={2}>
          <Text fontSize="sm" color="gray.600">Stage:</Text>
          <Select
            size="sm"
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            maxW="220px"
          >
            <option value="all">All stages</option>
            {Array.from(VISIBLE_STAGE).map((v) => {
              const opt = STAGE_OPTIONS.find((o) => o.value === v);
              return (
                <option key={v} value={v}>
                  {opt?.label || v}
                </option>
              );
            })}
          </Select>
        </HStack>

        {/* New: Sorting controls */}
        <HStack spacing={2}>
          <Text fontSize="sm" color="gray.600">Sort:</Text>
          <Select
            size="sm"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            maxW="200px"
          >
            <option value="appt">Appointment date</option>
            <option value="surveyDate">Survey date</option>
            <option value="surveyor">Surveyor</option>
          </Select>
          <Select
            size="sm"
            value={sortDir}
            onChange={(e) => setSortDir(e.target.value)}
            maxW="160px"
          >
            {dirOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </HStack>
      </HStack>

      {loading ? (
        <HStack>
          <Spinner />
          <Text>Loading…</Text>
        </HStack>
      ) : (
        <>
          <Table size="sm" variant="striped" sx={{ tableLayout: 'fixed' }}>
            <Thead>
              <Tr>
                <Th>Customer</Th>
                <Th w={COL.status}>Status</Th>
                <Th w={COL.stage}>Stage</Th>
                <Th minW={COL.surveyorMin}>Surveyor</Th>
                <Th w={COL.appt}>Appt</Th>
                <Th w={COL.survey}>Survey</Th>
                <Th w={COL.assigned}>Assigned</Th>
                <Th textAlign="right" w={COL.deal}>Deal Check</Th>
              </Tr>
            </Thead>
            <Tbody>
              {sorted.map((r) => {
                const tel = (r.customerPhone || '').replace(/[^\d+]/g, '');
                const telHref = tel ? `tel:${tel}` : null;
                const stage = r.postSaleStage || 'awaiting_survey';

                const apptDateObj = toDateObj(r.appt) || toDateObj(r.apptStartAt);
                const apptDate = fmtDate(apptDateObj);
                const apptTime = fmtTime(apptDateObj);

                const datePart = r.surveyDate
                  ? (() => {
                      try {
                        const [y, m, d] = String(r.surveyDate).split('-');
                        if (y && m && d) return `${d}/${m}/${String(y).slice(2)}`;
                        return '—';
                      } catch { return '—'; }
                    })()
                  : '—';

                const rawSlot = r.surveySlot || (r.surveyTime ? labelFromStart(r.surveyTime) : '');
                const slotPart = rawSlot ? normaliseSlot(rawSlot) : '—';
                const assignedYes = stage === 'survey_assigned';

                return (
                  <Tr key={r.leadId}>
                    <Td>
                      <Text fontWeight="semibold" noOfLines={1}>{r.customer}</Text>
                      <Text fontSize="xs" color="gray.600" noOfLines={2}>
                        {r.customerPhone && telHref ? (
                          <a href={telHref}>📞 {r.customerPhone}</a>
                        ) : r.customerPhone ? (
                          `📞 ${r.customerPhone}`
                        ) : null}
                        {r.customerPhone ? ' • ' : ''}
                        {r.postcode || '—'}
                        {r.addressLine1 || r.addressLine2 ? ' • ' : ''}
                        {[r.addressLine1, r.addressLine2].filter(Boolean).join(', ')}
                      </Text>
                    </Td>

                    <Td w={COL.status}>
                      <Tag colorScheme="green">SOLD</Tag>
                    </Td>

                    <Td w={COL.stage}>
                      <Text noOfLines={1}>{stageLabel(stage)}</Text>
                    </Td>

                    <Td minW={COL.surveyorMin}>
                      <Text noOfLines={1}>{r.surveyorName || '—'}</Text>
                    </Td>

                    <Td w={COL.appt}>
                      <Box>
                        <Text noOfLines={1}>{apptDate || '—'}</Text>
                        <Text fontSize="xs" color="gray.600" noOfLines={1}>{apptDateObj ? apptTime : '—'}</Text>
                      </Box>
                    </Td>

                    <Td w={COL.survey}>
                      <Box>
                        <Text noOfLines={1}>{datePart}</Text>
                        <Text fontSize="xs" color="gray.600" noOfLines={1}>{slotPart}</Text>
                      </Box>
                    </Td>

                    <Td w={COL.assigned}>
                      <Tag colorScheme={assignedYes ? 'green' : 'orange'}>
                        {assignedYes ? 'Yes' : 'No'}
                      </Tag>
                    </Td>

                    <Td textAlign="right" w={COL.deal}>
                      <Tooltip label="Open Deal Check">
                        <Button
                          size="xs"
                          leftIcon={<ExternalLinkIcon />}
                          colorScheme="blue"
                          variant="solid"
                          onClick={() => setDealRow(r)}
                        >
                          Deal Check
                        </Button>
                      </Tooltip>
                    </Td>
                  </Tr>
                );
              })}
              {sorted.length === 0 && (
                <Tr>
                  <Td colSpan={8}>
                    <Text color="gray.600">No sold leads match your filters.</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>

          <HStack mt={4}>
            <Button onClick={onLoadMore} isDisabled={!cursor} isLoading={loadingMore}>
              Load more
            </Button>
            {!cursor && <Text color="gray.500" fontSize="sm">All done.</Text>}
          </HStack>

          <DealCheckDrawer
            isOpen={!!dealRow}
            onClose={() => setDealRow(null)}
            row={dealRow}
            handlers={handlers}
          />
        </>
      )}
    </>
  );
}
